import { QueryHookResult, useGenericQuery } from '../useGenericQuery'
import { queryKeys } from '../queryRegistry'
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';

export type CalendarEvent = {
  eventType: string;
  id: number;
  title: string;
  from: string | Date;
  to: string | Date | null;
  start: Date;
  end: Date;
  idEmployee: number | null;
  employeeName: string | null;
  employeeLastName: string | null;
  idWorkflowState: number | null;
  workflowStateName: string | null;
  workflowStateColor: string | null;
  changeLog: string | null;
  idTimeOffRequestType: number | null;
  timeOffRequestTypeName: string | null;
  timeOffTypeName?: string;
  idCountry: number | null;
  country?: {
    name: string;
    code: string;
    phonePrefix: string;
    id: number;
    isDeleted: boolean;
  };
};

export const useCalendarEvents = (
  start?: Date,
  end?: Date,
  eventType?: string,
  enabled: boolean = true
): QueryHookResult<CalendarEvent> => {
  const { timeoffApi }: Api = useApi();
  
  const result = useGenericQuery<CalendarEvent>({
    queryKey: queryKeys.calendarEvents(start, end, eventType),
    queryFn: async (): Promise<CalendarEvent[]> => {
      const response = await timeoffApi.apiVversionEventAllGet("1", start, end);
      const { data } = response?.data;
      
      if (!data?.queryResult) return [];
      
      // Filter by eventType if provided
      const filteredResults = eventType 
        ? data.queryResult.filter((event: any) => event.eventType === eventType)
        : data.queryResult;
      
      // Transform the events to include start/end dates
      const renamedEvents = filteredResults.map((event: any) => ({
        ...event,
        start: new Date(event.from),
        end: new Date(event.to || event.from),
        title: event.title || `${event.timeOffRequestTypeName || event.timeOffTypeName} - ${event.employeeName} ${event.employeeLastName}`,
        workflowStateColor: event.workflowStateColor,
      }));
      
      return renamedEvents || [];
    },
    enabled
  });

  // Ensure dates are always Date objects (React Query cache converts them to strings)
  const eventsWithDateObjects = result.data.map(event => ({
    ...event,
    start: event.start instanceof Date ? event.start : new Date(event.start),
    end: event.end instanceof Date ? event.end : new Date(event.end),
  }));

  return {
    ...result,
    data: eventsWithDateObjects
  };
};

export const useCalendarEventsSearch = (
  term?: string,
  order?: string,
  filters?: Array<string>,
  enabled: boolean = true
): QueryHookResult<CalendarEvent> => {
  const { timeoffApi }: Api = useApi();
  
  const result = useGenericQuery<CalendarEvent>({
    queryKey: queryKeys.calendarEventsSearch(undefined, undefined, term, filters),
    queryFn: async (): Promise<CalendarEvent[]> => {
      const response = await timeoffApi.apiVversionEventSearchGet("1", term, order, undefined, filters);
      const { data } = response?.data;
      
      if (!data?.queryResult) return [];
      
      const renamedEvents = data.queryResult.map((event: any) => ({
        ...event,
        start: new Date(event.from),
        end: new Date(event.to || event.from),
        from: new Date(event.from),
        to: new Date(event.to || event.from),
        timeOffRequestTypeName: event.timeOffRequestTypeName,
        employeeName: event.employeeName,
        employeeLastName: event.employeeLastName,
        workflowStateName: event.workflowStateName,
        workflowStateColor: event.workflowStateColor,
        timeOffRequestTypeLabel: event.timeOffRequestTypeLabel,
      }));
      
      return renamedEvents || [];
    },
    enabled
  });

  // Ensure dates are always Date objects (React Query cache converts them to strings)
  const eventsWithDateObjects = result.data.map(event => ({
    ...event,
    start: event.start instanceof Date ? event.start : new Date(event.start),
    end: event.end instanceof Date ? event.end : new Date(event.end),
    from: event.from instanceof Date ? event.from : (event.from ? new Date(event.from) : event.from),
    to: event.to instanceof Date ? event.to : (event.to ? new Date(event.to) : event.to),
  }));

  return {
    ...result,
    data: eventsWithDateObjects
  };
};

